import { Button, Input, useDebounceCallback } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";
import type {
  FilterConditionDTO,
  OffsetPageQueryDTO,
} from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
} from "@/shared/contracts/query-helpers";
import { config } from "@/shared/lib/config";
import { fetchLookupForInfinite, hydrateLookupItem } from "@/shared/lib/utils";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";

type DepartmentItem = { id: string; name: string };

export function TemplateByDepartmentFilter() {
  const nav = useNavigate({ from: "/app/template/by-department" });
  const search = useSearch({ from: "/app/template/by-department" }) as OffsetPageQueryDTO;
  const filters = (search.filters as FilterConditionDTO[] | undefined) ?? [];

  const nameFilter = findCondition(filters, "name");
  const idFilter = findCondition(filters, "id");

  const [searchValue, setSearchValue] = useState<string>(
    (nameFilter?.value as string) || "",
  );

  const [selectedDepartmentId, setSelectedDepartmentId] = useState<string>(
    (idFilter?.value as string) || "",
  );

  useEffect(
    () => setSearchValue((nameFilter?.value as string) || ""),
    [nameFilter?.value],
  );

  useEffect(
    () => setSelectedDepartmentId((idFilter?.value as string) || ""),
    [idFilter?.value],
  );

  const debounced = useDebounceCallback((val: string) => {
    setSearchValue(val);
    let nextFilters: FilterConditionDTO[] | undefined = filters;
    if (val) {
      nextFilters = upsertCondition(filters, {
        field: "name",
        op: "contains",
        value: val,
      });
    } else {
      nextFilters = removeConditions(filters, "name");
    }
    nav({
      search: {
        ...search,
        offset: 0,
        filters: nextFilters?.length ? nextFilters : undefined,
      },
    });
  }, 400);

  const handleDepartmentChange = (departmentId: string) => {
    setSelectedDepartmentId(departmentId);
    let nextFilters: FilterConditionDTO[] | undefined = filters;
    if (departmentId) {
      nextFilters = upsertCondition(filters, {
        field: "id",
        op: "eq",
        value: departmentId,
      });
    } else {
      nextFilters = removeConditions(filters, "id");
    }
    nav({
      search: {
        ...search,
        offset: 0,
        filters: nextFilters?.length ? nextFilters : undefined,
      },
    });
  };

  const hasFilter = !!nameFilter?.value || !!idFilter?.value;

  return (
    <div className="mb-4 flex items-center justify-between gap-4 px-2" data-tourid="by-department-filter">
      <div className="flex flex-1 flex-wrap items-center gap-2">
        {/* <Input
          placeholder="ຄົ້ນຫາພະແນກ..."
          defaultValue={searchValue}
          onChange={(e) => debounced(e.target.value)}
          className="h-8 sm:max-w-xs"
        /> */}

        <InfiniteCombobox<DepartmentItem>
          value={selectedDepartmentId}
          onValueChange={handleDepartmentChange}
          queryKey={["departments-lookup"]}
          queryFn={(args) =>
            fetchLookupForInfinite(
              `${config.apiUrl}/departments/lookup`,
              args,
            )
          }
          preloadQueryFn={(id) =>
            hydrateLookupItem(`${config.apiUrl}/departments/lookup`, id)
          }
          getLabel={(item) => item.name}
          getValue={(item) => item.id}
          placeholder="ເລືອກພະແນກ"
          className="h-8 sm:max-w-xs"
          clearable
        />
      </div>
      {hasFilter && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            nav({ search: { ...search, filters: undefined, offset: 0 } })
          }
          className="max-sm:w-full"
        >
          <XIcon className="h-4 w-4" /> ລ້າງ
        </Button>
      )}
    </div>
  );
}
